import { canEnablePushNotifications, detectPushPlatform, isPushApiSupported } from './support';
import { fetchVapidPublicKey, registerPushSubscription, removePushSubscription } from './api';
import { urlBase64ToUint8Array } from './urlBase64ToUint8Array';
import { PushSubscriptionPayload } from './types';

function toSubscriptionPayload(subscription: PushSubscription): PushSubscriptionPayload {
  const json = subscription.toJSON();
  const { endpoint, keys, expirationTime } = json;
  const p256dh = keys?.p256dh;
  const auth = keys?.auth;

  if (!endpoint || !p256dh || !auth) {
    throw new Error('Subscription inválida: endpoint ou keys ausentes');
  }

  return {
    endpoint,
    keys: {
      p256dh,
      auth,
    },
    expirationTime: expirationTime ?? null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    platform: detectPushPlatform(),
  };
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.ready;
  return registration;
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushApiSupported()) {
    return null;
  }

  const registration = await getServiceWorkerRegistration();
  return registration.pushManager.getSubscription();
}

export async function subscribeToPushNotifications(): Promise<PushSubscription> {
  if (!canEnablePushNotifications()) {
    throw new Error('Notificações push não estão disponíveis neste dispositivo ou modo');
  }

  const publicKey = await fetchVapidPublicKey();
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  const registration = await getServiceWorkerRegistration();

  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    if (Notification.permission !== 'granted') {
      throw new Error('Permissão de notificação negada');
    }

    await registerPushSubscription(toSubscriptionPayload(existing));
    return existing;
  }

  if (Notification.permission === 'denied') {
    throw new Error('Permissão de notificação negada');
  }

  if (Notification.permission === 'default') {
    const requestedPermission = await Notification.requestPermission();
    if (requestedPermission !== 'granted') {
      throw new Error('Permissão de notificação negada');
    }
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey as BufferSource,
  });

  await registerPushSubscription(toSubscriptionPayload(subscription));
  return subscription;
}

export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!isPushApiSupported()) {
    return;
  }

  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return;
  }

  const { endpoint } = subscription;

  try {
    await removePushSubscription(endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}

export async function renewPushSubscription(): Promise<void> {
  if (!canEnablePushNotifications()) {
    return;
  }

  if (Notification.permission !== 'granted') {
    return;
  }

  await subscribeToPushNotifications();
}
